import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { Location, useLocation, useNavigate } from "react-router";
import { TextUtil } from "src/util/TextUtil";

export type TabHashKey = 'tab' | 'subTab';
export type TabHash = Partial<Record<TabHashKey, string>>;

/** Hash-safe form of a tab key: dynamic pages key tabs by title, static pages by item key. */
export function getTabKey(tab: string | number | { title: string; } | undefined) {
  const raw = typeof tab === 'object' ? tab?.title : tab;
  return raw == null ? undefined : TextUtil.removeWhitespace(String(raw));
}

/** Position within sticky tabs: nested in a pane at all, and whether that pane is the visible one. */
export const TabPaneContext = createContext({ nested: false, active: true });

export function parseHash(location: Location): Record<string, string> {
  const hash = location.hash.substring(1);
  if (!hash) return {};
  return hash.split('&').map(kv => kv.split('='))
    .reduce((prev, [key, value]) => ({ ...prev, [key]: TextUtil.removeWhitespace(decodeURIComponent(value)) }), {});
}

export function hashRecordToStr(hashObj: Record<string, string>) {
  const hashes = [];
  for (const key in hashObj) {
    if (key && Object.hasOwn(hashObj, key)) {
      hashes.push(`${key}=${encodeURIComponent(String(hashObj[key]))}`);
    }
  }

  return hashes.length ? '#' + hashes.join('&') : '';
}

/**
 * Drives antd Tabs from the URL hash (`#tab=...&subTab=...`) so a static page tab is reachable by link.
 * @param keys keys of the rendered tabs; a hash value outside them falls back to `defaultKey`, then the first key
 * @param defaultKey key used when the hash has no valid value
 * @param hashKey hash entry for this tab level; derived from the enclosing tabs when omitted
 * @returns controlled `activeKey` and `onChange` for antd Tabs
 */
export function useHashTab<K extends string | number>(keys: readonly K[] | undefined, defaultKey?: K, hashKey?: TabHashKey) {
  const location = useLocation();
  const navigate = useNavigate();
  const pane = useContext(TabPaneContext);
  const key = hashKey ?? (pane.nested ? 'subTab' : 'tab');
  const requested = parseHash(location)[key];
  const found = keys?.find(k => getTabKey(k) === requested);

  const writeHash = useCallback((change: (hash: Record<string, string>) => void) => {
    const hash = parseHash(location);
    change(hash);
    // nested selection belongs to the previous top tab
    if (key === 'tab') delete hash.subTab;
    navigate(decodeURIComponent(location.pathname + location.search) + hashRecordToStr(hash), { replace: true });
  }, [location, navigate, key]);

  // a stale entry is dropped only by the page owning the path: the outgoing page still renders once with the new location
  const ownPath = useRef(location.pathname + location.search);
  const stale = requested !== undefined && !found && !!keys?.length && pane.active
    && ownPath.current === location.pathname + location.search;
  useEffect(() => {
    if (stale) writeHash(hash => { delete hash[key]; });
  }, [stale, key, writeHash]);

  return {
    activeKey: found ?? defaultKey ?? keys?.[0],
    onChange: (tabKey: string) => writeHash(hash => { hash[key] = getTabKey(tabKey); }),
  };
}
